import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const TermsConditionsPage = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-[#0a1628] py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <Link to="/" className="text-[#00bcd4] hover:text-[#00acc1] text-sm flex items-center gap-1 mb-4">
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </Link>
          <h1 className="font-serif text-4xl md:text-5xl font-bold text-white" data-testid="terms-conditions-title">Terms &amp; Conditions</h1>
          <p className="text-white/70 mt-3">Please read these terms carefully before booking</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-12 md:py-16" data-testid="terms-conditions-content">
        <div className="prose prose-lg max-w-none">
          <p className="text-muted-foreground mb-8">
            Last updated: February 2026
          </p>

          <section className="mb-10">
            <h2 className="font-serif text-2xl font-bold mb-4">1. Acceptance of Terms</h2>
            <p className="text-muted-foreground leading-relaxed">
              By accessing and using the website of The Village (operated by Harbor Point Holdings) and by making a booking through our platform, you agree to be bound by these Terms and Conditions. If you do not agree to any part of these terms, you should not use this website or make a booking.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="font-serif text-2xl font-bold mb-4">2. Business Information</h2>
            <p className="text-muted-foreground leading-relaxed">
              <strong>Legal Business Name:</strong> Harbor Point Holdings<br />
              <strong>Doing Business As (DBA):</strong> The Village<br />
              <strong>Location:</strong> Athol Island, Nassau, The Bahamas<br />
              <strong>Contact:</strong> +1 (242) 825-6265 | hello@thevillageatholisland.com<br />
              <strong>Hours of Operation:</strong> Daily, 9:00 AM - 5:00 PM (Eastern)
            </p>
          </section>

          <section className="mb-10">
            <h2 className="font-serif text-2xl font-bold mb-4">3. Services Offered</h2>
            <p className="text-muted-foreground leading-relaxed">
              The Village (operated by Harbor Point Holdings) provides guided tour experiences including boat transportation, snorkelling, dining, and cultural entertainment at Athol Island, Nassau, The Bahamas. All services are fulfilled on-site and do not involve the shipment of physical goods. The tour described on this website constitutes the complete scope of the service offering.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="font-serif text-2xl font-bold mb-4">4. Booking and Payment</h2>
            <div className="text-muted-foreground leading-relaxed space-y-3">
              <p>All prices displayed on this website are in <strong>Bahamian Dollars (BSD)</strong>. The Bahamian Dollar is pegged 1:1 with the US Dollar.</p>
              <p>By placing a booking, you are making an offer to purchase the tour service at the price indicated. A booking is confirmed once you receive a booking confirmation with a QR code.</p>
              <p>Payment methods accepted are as indicated at checkout. When cash payment on arrival is the active payment method, full payment in cash (BSD or USD accepted) is required upon check-in at the tour departure point.</p>
              <p>When credit or debit card payment is available, your card will be charged the full amount at the time of booking. We accept Visa and Mastercard.</p>
            </div>
          </section>

          <section className="mb-10">
            <h2 className="font-serif text-2xl font-bold mb-4">5. Cancellation and Refund Policy</h2>
            <div className="text-muted-foreground leading-relaxed space-y-3">
              <p>Our full cancellation policy is available at <Link to="/cancellation-policy" className="text-primary hover:underline">Cancellation Policy</Link>. In summary:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>72+ hours before tour:</strong> Full refund (100%)</li>
                <li><strong>24-72 hours before tour:</strong> Partial refund (50%)</li>
                <li><strong>Less than 24 hours / no-show:</strong> No refund</li>
              </ul>
              <p>Refunds for card payments will be processed back to the original payment method within 5-10 business days. Weather-related cancellations by the operator will receive a full refund or free rescheduling.</p>
            </div>
          </section>

          <section className="mb-10">
            <h2 className="font-serif text-2xl font-bold mb-4">6. Service Fulfilment</h2>
            <div className="text-muted-foreground leading-relaxed space-y-3">
              <p>Tours operate as scheduled on the date and time selected during booking. Check-in begins 30 minutes prior to the scheduled departure time at the designated meeting point (Montague Dock, Nassau).</p>
              <p>The Village (operated by Harbor Point Holdings) reserves the right to modify the itinerary due to weather conditions, safety concerns, or operational requirements. In such cases, you will be notified as soon as practicable and offered a reschedule or refund.</p>
              <p>The tour experience is a same-day service. There are no multiple shipments or delayed deliveries. All services are rendered on the booked date.</p>
            </div>
          </section>

          <section className="mb-10">
            <h2 className="font-serif text-2xl font-bold mb-4">7. Customer Responsibilities</h2>
            <div className="text-muted-foreground leading-relaxed space-y-3">
              <p>Guests must arrive at the check-in point on time. Late arrivals may not be accommodated and will be treated as a no-show.</p>
              <p>Guests participate in all water activities at their own risk. All guests must follow safety instructions provided by tour guides and crew.</p>
              <p>Guests must inform us of any medical conditions, allergies, or special requirements at the time of booking.</p>
            </div>
          </section>

          <section className="mb-10">
            <h2 className="font-serif text-2xl font-bold mb-4">8. Limitation of Liability</h2>
            <p className="text-muted-foreground leading-relaxed">
              Harbor Point Holdings d/b/a The Village shall not be liable for any indirect, incidental, or consequential damages arising from the use of this website or participation in tour activities, except where prohibited by law. Our total liability shall not exceed the amount paid for the booking in question.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="font-serif text-2xl font-bold mb-4">9. Privacy</h2>
            <p className="text-muted-foreground leading-relaxed">
              Your personal information is collected and processed in accordance with our <Link to="/privacy-policy" className="text-primary hover:underline">Privacy Policy</Link>. We collect only the information necessary to process your booking and provide our services. We do not sell or share your personal information with third parties except as required to fulfil your booking or comply with law.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="font-serif text-2xl font-bold mb-4">10. Transaction Records</h2>
            <p className="text-muted-foreground leading-relaxed">
              We strongly advise all customers to retain a copy of their booking confirmation and any transaction receipts for their records. Transaction details are available for a minimum of 120 days from the date of the transaction upon request.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="font-serif text-2xl font-bold mb-4">11. Governing Law</h2>
            <p className="text-muted-foreground leading-relaxed">
              These Terms and Conditions shall be governed by and construed in accordance with the laws of the Commonwealth of The Bahamas. Any disputes arising from these terms shall be subject to the exclusive jurisdiction of the courts of The Bahamas.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="font-serif text-2xl font-bold mb-4">12. Contact Us</h2>
            <p className="text-muted-foreground leading-relaxed">
              If you have any questions about these Terms and Conditions, please contact us:<br /><br />
              <strong>Harbor Point Holdings d/b/a The Village</strong><br />
              Athol Island, Nassau, The Bahamas<br />
              Phone: +1 (242) 825-6265<br />
              Email: hello@thevillageatholisland.com<br />
              Hours: Daily, 9:00 AM - 5:00 PM (Eastern)
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TermsConditionsPage;
